import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import StagesStatusSection from "./StagesStatus";
import {
  GET_LEAD_DETAILS,
  GET_LEAD_ACTIVITY,
  CURRENT_USER_PROFIILE,
  GET_ALL_ACTIVITIES,
  GET_ALL_STATUS_FOR_ACTIVITY,
  ADD_UPDATE_LEAD_ACTIVITY_LOG,
  UPDATE_LEADS_STAGE_REMARKS,
  VALIDATION_TO_MOVE_LEAD_TO_SALES,
  MOVE_LEAD_FROM_PRESALES_TO_SALES
} from "../services/userApi";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "./breadcrumb";
import BackButton from "../reusableField/BackButton";
import { BASE_URL } from "./apiConfig";
import {
  message,
  ConfigProvider,
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
} from "antd";
import {
  Phone,
  Mail,
  Briefcase,
  Building2,
  MapPin,
  MessageSquareText,
  Cpu,
  User,
  mapPin
  // Linkedin
} from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import "../Home/global.css";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  CalendarToday as CalendarTodayIcon,
  Message as MessageIcon,
  EditNote as EditNoteIcon,
  Timer as TimerIcon,
  History as HistoryIcon,
  Schedule,
  LocationOn,
  SwapHoriz,
  AccessTime as AccessTimeIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationOnIcon,
  LabelOutlined as LabelIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const ActivityTimeline = ({ data, onEditActivity, onEditRemarks }) => {
  const [expandedRemarks, setExpandedRemarks] = useState({});

  const getMeetingMomDownloadUrl = (filePath) => {
    if (!filePath) return "";
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const normalizedPath = filePath.startsWith("/") ? filePath : `/${filePath}`;
    return `${BASE_URL}${normalizedPath}`;
  };

  const toggleRemarks = (id) => {
    setExpandedRemarks((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  };

  const getFormatDateTime = (isoString) => {
    const dateObj = new Date(isoString);

    const date = dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const time = dateObj.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date, time };
  };

  const getStatusColor = (statusName) => {
    const colors = {
      NEW: "info",
      WON: "success",
      LOST: "error",
      "ON HOLD": "warning",
      EXISTING: "default",
      connected: "success",
      disconnected: "error",
      busy: "warning",
    };
    return colors[statusName] || "default";
  };

  const RemarksSection = ({ remarks, id, logType, onEdit }) => {
    const isLong = remarks && remarks.length > 60;
    const isExpanded = expandedRemarks[id];
    const displayText = remarks
      ? isLong && !isExpanded
        ? `${remarks.substring(0, 60)}...`
        : remarks
      : "No remarks";

    return (
      <Box
        className="global"
        sx={{
          mt: 0.5,
          p: 1,
          bgcolor: "#f8f9fa",
          borderRadius: 0.5,
          border: "1px solid #e9ecef",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {displayText}
          </Typography>
          {isLong && (
            <IconButton
              size="small"
              onClick={() => toggleRemarks(id)}
              sx={{ p: 0.25, color: "primary.main", ml: 0.5 }}
            >
              {isExpanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
        {onEdit && (
          <IconButton
            size="small"
            onClick={() => onEdit(id, remarks || "", logType)}
            sx={{
              p: 0.5,
              color: "#c12328",
              "&:hover": { bgcolor: "#FAC9CA" },
            }}
            title="Edit remarks"
          >
            <EditNoteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderStageChange = (log) => {
    const {
      fromStage,
      toStage,
      ldmsStatus,
      createdAt,
      remarks,
      transitionTimeSec,
      backDatedEntry,
    } = log.log;

    let transitionDaysCalc = transitionTimeSec;
    if (transitionTimeSec === null) {
      transitionDaysCalc = 0;
    }
    const transitionDays = transitionDaysCalc
      ? Math.floor(transitionDaysCalc / (24 * 60 * 60))
      : null;

    return (
      <Card variant="outlined" sx={{ boxShadow: 1 }} className="global">
        <CardContent
          sx={{
            py: 1,
            px: 1.5,
            "&:last-child": { pb: 1 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <SwapHoriz sx={{ fontSize: 16, color: "primary.main" }} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: "0.85rem" }}
            >
              {fromStage.stageName} → {toStage.stageName}
            </Typography>
            {backDatedEntry && (
              <Chip
                label="Backdated"
                size="small"
                icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#FF9800",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            )}
            {transitionDays !== null && (
              <Chip
                label={`${transitionDays} ${transitionDays === 1 ? "day" : "days"
                  }`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  color: "#c12328",
                  borderColor: "#c12328",
                }}
              />
            )}
          </Stack>

          {backDatedEntry && (
            <Box
              sx={{
                mt: 0.5,
                mb: 0.5,
                p: 1,
                bgcolor: "#FFF3E0",
                borderRadius: 0.5,
                border: "1px solid #FFB74D",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "#E65100",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <HistoryIcon sx={{ fontSize: 12 }} />
                Logged on {getFormatDateTime(createdAt).date} at{" "}
                {getFormatDateTime(createdAt).time}
              </Typography>
            </Box>
          )}

          <RemarksSection
            remarks={remarks}
            id={log.log.id}
            logType="stage_change"
            onEdit={onEditRemarks}
          />
        </CardContent>
      </Card>
    );
  };

  const renderStatusChange = (log) => {
    const { toStage, ldmsStatus, createdAt, remarks, backDatedEntry } = log.log;

    return (
      <Card variant="outlined" sx={{ boxShadow: 1 }} className="global">
        <CardContent
          sx={{
            py: 1,
            px: 1.5,
            "&:last-child": { pb: 1 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: "0.85rem" }}
            >
              {toStage.stageName} : {ldmsStatus.statusName}
            </Typography>
            {backDatedEntry && (
              <Chip
                label="Backdated"
                size="small"
                icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#FF9800",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            )}
          </Stack>

          {backDatedEntry && (
            <Box
              sx={{
                mt: 0.5,
                mb: 0.5,
                p: 1,
                bgcolor: "#FFF3E0",
                borderRadius: 0.5,
                border: "1px solid #FFB74D",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "#E65100",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <HistoryIcon sx={{ fontSize: 12 }} />
                Logged on {getFormatDateTime(createdAt).date} at{" "}
                {getFormatDateTime(createdAt).time}
              </Typography>
            </Box>
          )}

          <RemarksSection
            remarks={remarks}
            id={log.log.id}
            logType="status_change"
            onEdit={onEditRemarks}
          />
        </CardContent>
      </Card>
    );
  };

  const renderActivity = (log) => {
    const {
      activity,
      activityStatus,
      activityTimestamp,
      createdAt,
      remarks,
      meeting,
      callDurationSeconds,
      backDatedEntry,
      meetingMomFilepath,
    } = log.log;

    return (
      <Card variant="outlined" sx={{ boxShadow: 1 }} className="global">
        <CardContent
          sx={{
            py: 1,
            px: 1.5,
            "&:last-child": { pb: 1 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {activity.activityName === "call" && (
                <PhoneIcon sx={{ fontSize: 16, color: "info.main" }} />
              )}
              {activity.activityName === "email" && (
                <EmailIcon sx={{ fontSize: 16, color: "red" }} />
              )}
              {activity.activityName === "sms" && (
                <MessageIcon sx={{ fontSize: 16, color: "orange" }} />
              )}
              {activity.activityName === "meeting" && (
                <CalendarTodayIcon sx={{ fontSize: 16, color: "info.main" }} />
              )}
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ textTransform: "capitalize", fontSize: "0.85rem" }}
              >
                {activity.activityName}
              </Typography>

              {backDatedEntry && (
                <Chip
                  label="Backdated"
                  size="small"
                  icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    bgcolor: "#FF9800",
                    color: "white",
                    fontWeight: 600,
                    "& .MuiChip-icon": {
                      color: "white",
                    },
                  }}
                />
              )}
              {activityStatus && (
                <Chip
                  label={activityStatus?.statusName || "No Status"}
                  color={getStatusColor(
                    activityStatus?.statusName || "No Status"
                  )}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {callDurationSeconds && activity.activityName === "call" && (
                <Chip
                  label={
                    <>
                      <TimerIcon
                        sx={{ fontSize: 16, mr: 0.5, color: "#F69193" }}
                      />
                      {`${Math.floor(callDurationSeconds / 60)}m ${callDurationSeconds % 60
                        }s`}
                    </>
                  }
                  variant="body2"
                  fontWeight={600}
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {meetingMomFilepath && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={() =>
                    window.open(getMeetingMomDownloadUrl(meetingMomFilepath), "_blank")
                  }
                  sx={{
                    textTransform: "none",
                    borderColor: "#c12328",
                    color: "#c12328",
                    fontFamily: "Inter",
                    fontSize: "0.7rem",
                    minWidth: "auto",
                    px: 1,
                    py: 0.25,
                    lineHeight: 1.2,
                    "&:hover": {
                      borderColor: "#a01f23",
                      bgcolor: "#fff5f5",
                    },
                  }}
                >
                  Download MOM
                </Button>
              )}
            </Stack>

            <IconButton
              size="small"
              onClick={() => onEditActivity && onEditActivity(log.log)}
              sx={{
                p: 0.5,
                color: "#666",
                "&:hover": {
                  color: "#c12328",
                  bgcolor: "#FAC9CA",
                },
              }}
            >
              <EditNoteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          {backDatedEntry && (
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                bgcolor: "#FFF3E0",
                borderRadius: 0.5,
                border: "1px solid #FFB74D",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "#E65100",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <HistoryIcon sx={{ fontSize: 12 }} />
                Logged on {getFormatDateTime(createdAt).date} at{" "}
                {getFormatDateTime(createdAt).time}
              </Typography>
            </Box>
          )}

          {meeting && (
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                bgcolor: "action.hover",
                borderRadius: 0.5,
              }}
            >
              <Stack spacing={0.25}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  <Schedule fontSize="inherit" sx={{ mr: 0.5 }} />
                  Scheduled For :
                  {getFormatDateTime(meeting.meetingStartDatetime).date}{" "}
                  {getFormatDateTime(meeting.meetingStartDatetime).time} -{" "}
                  {getFormatDateTime(meeting.meetingEndDatetime).time}
                </Typography>
                {meeting.location && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    <LocationOn fontSize="inherit" sx={{ mr: 0.5 }} />
                    {meeting.location}
                  </Typography>
                )}
                {/* <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Status: {meeting.meetingStatus}
                </Typography> */}
              </Stack>
            </Box>
          )}

          <RemarksSection remarks={remarks} id={log.log.id} />
        </CardContent>
      </Card>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Schedule sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          No activities yet
        </Typography>
      </Box>
    );
  }

  const sortedData = [...data].sort(
    (a, b) => new Date(b.log.createdAt) - new Date(a.log.createdAt)
  );

  return (
    <Timeline
      position="right"
      sx={{
        fontFamily: "Inter",
        p: 0,
        m: 0,
        "& .MuiTimeline-root": {
          p: 0,
        },
      }}
    >
      {data.map((item, index) => {
        const isBackdated = item.log.backDatedEntry;
        const displayDate =
          isBackdated && item.log.activityTimestamp
            ? item.log.activityTimestamp
            : item.log.createdAt;

        const { date, time } = formatDateTime(displayDate);
        const isActivity = !!item.log.activity;
        const actionType = item.log.actionType;

        return (
          <TimelineItem
            key={item.log.id}
            sx={{
              fontFamily: "Inter",
              minHeight: "auto",
              "&:before": {
                content: "none",
              },
            }}
          >
            <TimelineSeparator sx={{ mr: 2 }}>
              <TimelineDot
                sx={{
                  fontFamily: "Inter",
                  bgcolor: isBackdated ? "#FF9800" : "#F69193",
                  width: 12,
                  height: 12,
                  boxShadow: 1,
                  margin: "8px 0",
                }}
              />
              {index < sortedData.length - 1 && (
                <TimelineConnector sx={{ minHeight: 20 }} />
              )}
            </TimelineSeparator>

            <TimelineContent sx={{ px: 0, pb: 1.5, pt: 0 }}>
              <Box sx={{ fontFamily: "Inter", mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {time} • {date}
                </Typography>
              </Box>

              {actionType === "stage_change" && renderStageChange(item)}
              {actionType === "status_change" && renderStatusChange(item)}
              {isActivity && renderActivity(item)}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

const ActivitiesSection = ({ allActivitiesData, onActivityClick }) => {
  const activities = allActivitiesData?.getAllActivitiesForUser || [];

  const getActivityIcon = (activityName) => {
    switch (activityName?.toLowerCase()) {
      case "call":
        return <PhoneIcon sx={{ fontSize: 20 }} />;
      case "email":
        return <EmailIcon sx={{ fontSize: 20 }} />;
      case "sms":
        return <MessageIcon sx={{ fontSize: 20 }} />;
      case "meeting":
        return <CalendarTodayIcon sx={{ fontSize: 20 }} />;
      case "whatsapp":
        return <WhatsAppIcon sx={{ fontSize: 20 }} />;
      case "remarks":
        return <EditNoteIcon sx={{ fontSize: 20 }} />;
      default:
        return <MessageIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getActivityColor = (activityName) => {
    const colors = {
      call: "#2196F3",
      email: "#F44336",
      sms: "#FF9800",
      meeting: "#9C27B0",
      whatsapp: "#25D366",
      remarks: "#FF5722",
    };
    return colors[activityName?.toLowerCase()] || "#757575";
  };

  return (
    <Box
      className="global"
      sx={{
        bgcolor: "white",
        borderRadius: 2,
        p: 3,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography
        className="global"
        variant="subtitle1"
        sx={{ mb: 2, fontWeight: 600, color: "#333" }}
      >
        Activities
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {activities.map((activity) => {
          const name = activity.activityName || "Unknown";
          const color = getActivityColor(name);

          return (
            <Box
              key={activity.id}
              onClick={() => onActivityClick && onActivityClick(activity)}
              sx={{
                px: 1.4,
                py: 0.7,
                borderRadius: "999px",
                border: `1px solid ${color}`,
                color,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                fontSize: "0.72rem",
                transition: "0.2s",
                background: "white",
                "&:hover": {
                  bgcolor: color,
                  color: "white",
                },
              }}
            >
              {getActivityIcon(name)}
              {name}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const EntryTypeModal = ({ open, onClose, onSelectType }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pt: 2, pb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: "Inter", fontWeight: 600 }}>
          Select Entry Type
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <Stack spacing={2}>
          <Button
            variant="outlined"
            onClick={() => onSelectType("log")}
            sx={{
              py: 2,
              borderColor: "#c12328",
              color: "#c12328",
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-start",
              textTransform: "none",
              fontFamily: "Inter",
              fontSize: "1rem",
              "&:hover": {
                bgcolor: "#FAC9CA",
                borderColor: "#c12328",
              },
            }}
          >
            <EditNoteIcon sx={{ fontSize: 24 }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="body1" fontWeight={600}>
                Log Entry
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Log an activity with date & time
              </Typography>
            </Box>
          </Button>

          <Button
            variant="outlined"
            onClick={() => onSelectType("scheduled")}
            sx={{
              py: 2,
              borderColor: "#c12328",
              color: "#c12328",
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-start",
              textTransform: "none",
              fontFamily: "Inter",
              fontSize: "1rem",
              "&:hover": {
                bgcolor: "#FAC9CA",
                borderColor: "#c12328",
              },
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 24 }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="body1" fontWeight={600}>
                Schedule
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Schedule this activity for later
              </Typography>
            </Box>
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#666",
            textTransform: "none",
            fontFamily: "Inter",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  pickerPopupProps,
}) => {
  return (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        bgcolor: "#fef7f7",
        borderRadius: 2,
        border: "1px solid #f69193",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          fontWeight: 600,
          fontFamily: "Inter",
          color: "#c12328",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 18 }} />
        Activity Date & Time
      </Typography>

      <Stack direction="row" spacing={2}>
        <AntDatePicker
          {...pickerPopupProps}
          value={date || null}
          onChange={(selectedDate) => onDateChange(selectedDate)}
          format="DD/MM/YYYY"
          style={{ width: "100%", height: 40 }}
          placeholder="Select date"
          allowClear={false}
        />

        <AntTimePicker
          {...pickerPopupProps}
          value={time || null}
          onChange={(selectedTime) => onTimeChange(selectedTime)}
          format="HH:mm"
          minuteStep={5}
          style={{ width: "100%", height: 40 }}
          placeholder="Select time"
          allowClear={false}
        />
      </Stack>

      <Typography
        variant="caption"
        sx={{
          mt: 1.5,
          display: "block",
          color: "#666",
          fontFamily: "Inter",
          fontStyle: "italic",
        }}
      >
        📍 Time will be recorded in IST (Indian Standard Time)
      </Typography>
    </Box>
  );
};

const ActivityModal = ({
  open,
  onClose,
  activity,
  statusData,
  statusLoading,
  savingActivity,
  selectedStatus,
  setSelectedStatus,
  remarks,
  setRemarks,
  callDuration,
  setCallDuration,
  meetingDate,
  setMeetingDate,
  meetingStartTime,
  setMeetingStartTime,
  meetingEndTime,
  setMeetingEndTime,
  meetingLocation,
  setMeetingLocation,
  meetingMomUpload,
  setMeetingMomUpload,
  existingMeetingMomFilepath,
  onSave,
  isScheduled,
  isEditMode,
  entryType,
  logEntryDate,
  logEntryTime,
  onLogEntryDateChange,
  onLogEntryTimeChange,
}) => {
  const statuses = statusData?.getAllStatusForActivity || [];
  const activityName = activity?.activityName?.toLowerCase();
  const brandColor = "#c12328";
  const pickerPopupProps = {
    getPopupContainer: (trigger) =>
      trigger?.closest(".MuiDialog-root") || document.body,
    popupStyle: { zIndex: 1600 },
  };

  const showStatus = activityName !== "remarks" && !isScheduled;
  const showCallFields = activityName === "call" && !isScheduled;
  const showMeetingFields = activityName === "meeting" || isScheduled;
  const showDateTimePicker = entryType === "log" && !isEditMode;
  const showMeetingMomUploader = activityName === "meeting";

  const getMeetingMomDownloadUrl = (filePath) => {
    if (!filePath) return "";
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const normalizedPath = filePath.startsWith("/") ? filePath : `/${filePath}`;
    return `${BASE_URL}${normalizedPath}`;
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter",
      borderRadius: "8px",
      fontSize: "0.9rem",
      "& fieldset": {
        borderColor: "#d0d0d0",
      },
      "&:hover fieldset": {
        borderColor: "#c12328",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#c12328",
        borderWidth: "1px",
      },
    },
  };

  const getActivityIcon = () => {
    switch (activityName) {
      case "call":
        return <PhoneIcon sx={{ fontSize: 24, color: "#2196F3" }} />;
      case "email":
        return <EmailIcon sx={{ fontSize: 24, color: "#F44336" }} />;
      case "sms":
        return <MessageIcon sx={{ fontSize: 24, color: "#FF9800" }} />;
      case "meeting":
        return <CalendarTodayIcon sx={{ fontSize: 24, color: "#9C27B0" }} />;
      case "whatsapp":
        return <WhatsAppIcon sx={{ fontSize: 24, color: "#25D366" }} />;
      case "remarks":
        return <EditNoteIcon sx={{ fontSize: 24, color: "#FF5722" }} />;
      default:
        return null;
    }
  };

  const getModalTitle = () => {
    if (isEditMode) {
      return activityName === "remarks"
        ? "Edit Remarks"
        : `Edit ${activity?.activityName}`;
    }
    if (isScheduled) {
      return `Schedule ${activity?.activityName}`;
    }
    return activityName === "remarks"
      ? "Add Remarks"
      : `Log ${activity?.activityName}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pt: 1, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {getActivityIcon()}
          <Typography
            variant="h6"
            sx={{ textTransform: "capitalize", fontFamily: "Inter" }}
          >
            {getModalTitle()}
          </Typography>
          {isScheduled && (
            <Chip
              label="Scheduled"
              size="small"
              sx={{
                bgcolor: "#c12328",
                color: "white",
                fontWeight: 600,
              }}
            />
          )}
          {entryType === "log" && !isEditMode && (
            <Chip
              label="Log Entry"
              size="small"
              sx={{
                bgcolor: "#FF9800",
                color: "white",
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 1, pt: 2 }}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: brandColor,
              colorPrimaryHover: brandColor,
              colorPrimaryActive: brandColor,
              colorInfo: brandColor,
            },
          }}
        >
          {statusLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: "#c12328" }} />
            </Box>
          ) : (
            <>
            {/* Date-Time Picker for Log Entry */}
            {showDateTimePicker && (
              <DateTimePicker
                date={logEntryDate}
                time={logEntryTime}
                onDateChange={onLogEntryDateChange}
                onTimeChange={onLogEntryTimeChange}
                pickerPopupProps={pickerPopupProps}
              />
            )}

            {/* Status Section */}
            {showStatus && (
              <Box sx={{ mb: 3, pt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontFamily: "Inter",
                    color: "#333",
                  }}
                >
                  Status <span style={{ color: "#c12328" }}>*</span>
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", gap: 1 }}
                >
                  {statuses.map((status) => (
                    <Chip
                      key={status.id}
                      label={status.statusName}
                      onClick={() => setSelectedStatus(status)}
                      variant={
                        selectedStatus?.id === status.id ? "filled" : "outlined"
                      }
                      sx={{
                        bgcolor:
                          selectedStatus?.id === status.id
                            ? "#c12328"
                            : "transparent",
                        color:
                          selectedStatus?.id === status.id ? "white" : "#333",
                        borderColor: "#c12328",
                        fontFamily: "Inter",
                        fontSize: "0.875rem",
                        textTransform: "capitalize",
                        "&:hover": {
                          bgcolor:
                            selectedStatus?.id === status.id
                              ? "#a01f23"
                              : "#FAC9CA",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Call Duration - Only for call activity and not scheduled */}
            {showCallFields && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1.5,
                    fontWeight: 400,
                    fontFamily: "Inter",
                    color: "#333",
                  }}
                >
                  Call Duration
                </Typography>
                <TextField
                  fullWidth
                  placeholder="mm:ss"
                  value={callDuration}
                  onChange={(e) => {
                    const input = e.target.value;
                    const digits = input.replace(/\D/g, "");

                    if (digits.length === 0) {
                      setCallDuration("");
                    } else if (digits.length <= 2) {
                      setCallDuration(digits);
                    } else if (digits.length <= 4) {
                      const minutes = digits.slice(0, -2);
                      const seconds = digits.slice(-2);
                      setCallDuration(`${minutes}:${seconds}`);
                    } else {
                      const minutes = digits.slice(0, 2);
                      const seconds = digits.slice(2, 4);
                      setCallDuration(`${minutes}:${seconds}`);
                    }
                  }}
                  variant="outlined"
                  inputProps={{
                    maxLength: 5,
                  }}
                  InputProps={{
                    endAdornment: (
                      <Schedule sx={{ color: "#999", fontSize: 20 }} />
                    ),
                  }}
                  sx={{
                    height: 40,
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Inter",
                      height: 50,
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#c12328",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#c12328",
                        borderWidth: "1px",
                      },
                    },
                  }}
                />
              </Box>
            )}

            {/* Meeting/Schedule Fields */}
            {showMeetingFields && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      fontFamily: "Inter",
                      color: "#333",
                    }}
                  >
                    {isScheduled ? "Schedule Time" : "Time Period"}
                  </Typography>

                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          mb: 0.5,
                          display: "block",
                          fontFamily: "Inter",
                          color: "#666",
                        }}
                      >
                        Date <span style={{ color: "#c12328" }}>*</span>
                      </Typography>
                      <AntDatePicker
                        {...pickerPopupProps}
                        value={
                          meetingDate
                            ? dayjs(meetingDate, "YYYY-MM-DD")
                            : null
                        }
                        onChange={(_, dateString) => setMeetingDate(dateString)}
                        format="YYYY-MM-DD"
                        placeholder="Select date"
                        style={{ width: "100%", height: 50 }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          mb: 0.5,
                          display: "block",
                          fontFamily: "Inter",
                          color: "#666",
                        }}
                      >
                        Start <span style={{ color: "#c12328" }}>*</span>
                      </Typography>
                      <AntTimePicker
                        {...pickerPopupProps}
                        value={
                          meetingStartTime
                            ? dayjs(meetingStartTime, "HH:mm")
                            : null
                        }
                        onChange={(_, timeString) =>
                          setMeetingStartTime(timeString)
                        }
                        format="HH:mm"
                        minuteStep={5}
                        placeholder="Start time"
                        style={{ width: "100%", height: 50 }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          mb: 0.5,
                          display: "block",
                          fontFamily: "Inter",
                          color: "#666",
                        }}
                      >
                        End <span style={{ color: "#c12328" }}>*</span>
                      </Typography>
                      <AntTimePicker
                        {...pickerPopupProps}
                        value={
                          meetingEndTime
                            ? dayjs(meetingEndTime, "HH:mm")
                            : null
                        }
                        onChange={(_, timeString) => setMeetingEndTime(timeString)}
                        format="HH:mm"
                        minuteStep={5}
                        placeholder="End time"
                        style={{ width: "100%", height: 50 }}
                      />
                    </Box>
                  </Stack>
                </Box>

                {!isScheduled && activityName === "meeting" && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        fontFamily: "Inter",
                        color: "#333",
                      }}
                    >
                      Location <span style={{ color: "#c12328" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter location"
                      value={meetingLocation}
                      onChange={(e) => setMeetingLocation(e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <LocationOn sx={{ color: "#999", fontSize: 20 }} />
                        ),
                      }}
                      sx={textFieldStyles}
                    />
                  </Box>
                )}
              </>
            )}

            {showMeetingMomUploader && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontFamily: "Inter",
                    color: "#333",
                  }}
                >
                  Minutes of Meeting (MOM)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{
                      textTransform: "none",
                      borderColor: "#c12328",
                      color: "#c12328",
                      fontFamily: "Inter",
                      "&:hover": {
                        borderColor: "#a01f23",
                        bgcolor: "#fff5f5",
                      },
                    }}
                  >
                    {meetingMomUpload ? "Change MOM File" : "Upload MOM File"}
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0] || null;
                        setMeetingMomUpload(selectedFile);
                      }}
                    />
                  </Button>

                  {meetingMomUpload && (
                    <Chip
                      label={meetingMomUpload.name}
                      size="small"
                      onDelete={() => setMeetingMomUpload(null)}
                      deleteIcon={<CloseIcon />}
                      sx={{ maxWidth: 280 }}
                    />
                  )}

                  {!meetingMomUpload && existingMeetingMomFilepath && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                      onClick={() =>
                        window.open(
                          getMeetingMomDownloadUrl(existingMeetingMomFilepath),
                          "_blank"
                        )
                      }
                      sx={{
                        textTransform: "none",
                        color: "#c12328",
                        fontFamily: "Inter",
                        "&:hover": { bgcolor: "#fff5f5" },
                      }}
                    >
                      Download Existing MOM
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {/* Remarks Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  fontFamily: "Inter",
                  color: "#333",
                }}
              >
                Remarks <span style={{ color: "#c12328" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Write remarks..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Inter",
                    "& fieldset": {
                      borderColor: "#d0d0d0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#c12328",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#c12328",
                      borderWidth: "1px",
                    },
                    fontSize: "0.9rem",
                  },
                }}
              />
            </Box>
            </>
          )}
        </ConfigProvider>
      </DialogContent>

      <DialogActions sx={{ pt: 1, pb: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#666",
            textTransform: "none",
            fontFamily: "Inter",
            fontSize: "0.95rem",
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={
            savingActivity ||
            !remarks ||
            (showStatus && !selectedStatus) ||
            (showMeetingFields &&
              (!meetingDate || !meetingStartTime || !meetingEndTime)) ||
            (!isScheduled && activityName === "meeting" && !meetingLocation)
          }
          sx={{
            bgcolor: "#c12328",
            textTransform: "none",
            fontFamily: "Inter",
            fontSize: "0.95rem",
            px: 3,
            "&:hover": { bgcolor: "#a01f23" },
            "&:disabled": { bgcolor: "#ccc" },
          }}
        >
          {savingActivity ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LeadDetails = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const navigate = useNavigate();
  const leadId = location.state?.leadId;
  const leadCategoryId = location.state?.leadCategoryId;
  const currentPage = location.state?.returnPage || 1;

  const [logEntryDate, setLogEntryDate] = useState(dayjs());
  const [logEntryTime, setLogEntryTime] = useState(dayjs());
  const [entryType, setEntryType] = useState(null);

  const [callDuration, setCallDuration] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");
  const [meetingLocation, setMeetingLocation] = useState("");
  const [meetingMomUpload, setMeetingMomUpload] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [entryTypeModalOpen, setEntryTypeModalOpen] = useState(false);
  const [pendingActivity, setPendingActivity] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);

  // Move to Sales flow
  const [moveToSalesConfirmOpen, setMoveToSalesConfirmOpen] = useState(false);
  const [moveToSalesError, setMoveToSalesError] = useState(null);

  const limit = 10;
  let cursorValue = null;
  const [pagingCriteria, setPagingCriteria] = useState({
    limit: limit,
    cursor: null,
    sortByField: "created_at",
    sortOrder: "desc",
  });

  const breadcrumbItems = [
    {
      label: "Leads",
      onClick: () =>
        navigate("/leads", {
          state: {
            returnPage: currentPage,
          },
        }),
    },
    { label: "Lead Profile", href: "/leads/leadprofile" },
  ];

  const { data: userProfileData } = useQuery(CURRENT_USER_PROFIILE);
  const jobRoleId = userProfileData?.currentUserProfile?.roleId;

  const { data, loading, error, refetch: refetchLeadDetails } = useQuery(GET_LEAD_DETAILS, {
    variables: { leadId: leadId },
    fetchPolicy: "network-only",
  });

  const [
    getActivityStatus,
    { data: statusData, loading: statusLoading, error: statusError },
  ] = useLazyQuery(GET_ALL_STATUS_FOR_ACTIVITY);

  const [addUpdateLeadActivity, { loading: savingActivity }] = useMutation(
    ADD_UPDATE_LEAD_ACTIVITY_LOG,
    {
      onCompleted: (data) => {
        if (data.addUpdateLeadActivity.success) {
          messageApi.success(
            data.addUpdateLeadActivity.message || "Activity saved successfully!"
          );
          activityData?.getLeadsAndActivityLogs &&
            fetchMore({
              variables: {
                leadId,
                pagingCriteria: {
                  limit: limit,
                  cursor: null,
                  sortByField: "created_at",
                  sortOrder: "desc",
                },
              },
            });
        } else {
          messageApi.error(
            data.addUpdateLeadActivity.message || "Failed to save activity"
          );
        }
      },
      onError: (error) => {
        messageApi.error(
          error.message || "Failed to save activity. Please try again."
        );
      },
    }
  );

  const [updateStageRemarks, { loading: updatingRemarks }] = useMutation(
    UPDATE_LEADS_STAGE_REMARKS,
    {
      onCompleted: (data) => {
        if (data.updateLeadStageRemarks.success) {
          messageApi.success("Remarks updated successfully!");
          activityDataRefetch();
          setEditingRemarksId(null);
          setEditingRemarksValue("");
        } else {
          messageApi.error(
            data.updateLeadStageRemarks.message || "Failed to update remarks"
          );
        }
      },
      onError: (error) => {
        messageApi.error(
          error.message || "Failed to update remarks. Please try again."
        );
      },
    }
  );

  // Validation query (lazy)
  const [validateMoveToSales, { loading: validating }] = useLazyQuery(
    VALIDATION_TO_MOVE_LEAD_TO_SALES,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const isEligible =
          data?.validationForMovingLeadsFromOneStageToAnother;
        if (isEligible) {
          setMoveToSalesError(null);
          setMoveToSalesConfirmOpen(true);
        } else {
          setMoveToSalesError(
            "For moving a lead to sales, it should be in last stage."
          );
        }
      },
      onError: (err) => {
        setMoveToSalesError(
          err.message || "Validation failed. Please try again."
        );
      },
    }
  );

  // Move mutation
  const [moveLeadToSales, { loading: movingToSales }] = useMutation(
    MOVE_LEAD_FROM_PRESALES_TO_SALES,
    {
      onCompleted: (data) => {
        if (data?.moveLeadFromPresalesToSales?.success) {
          messageApi.success(
            data.moveLeadFromPresalesToSales.message ||
            "Lead successfully moved to Sales!"
          );
          setMoveToSalesConfirmOpen(false);
          // navigate(-1);
        } else {
          messageApi.error(
            data?.moveLeadFromPresalesToSales?.message ||
            "Failed to move lead to Sales."
          );
          setMoveToSalesConfirmOpen(false);
        }
      },
      onError: (err) => {
        messageApi.error(
          err.message || "Failed to move lead to Sales. Please try again."
        );
        setMoveToSalesConfirmOpen(false);
      },
    }
  );

  const handleMoveToSalesClick = () => {
    // Clear any previous error
    setMoveToSalesError(null);
    if (!lead?.stage?.id || !leadCategoryId) {
      setMoveToSalesError(
        "Lead stage or category information is missing. Cannot validate."
      );
      return;
    }
    validateMoveToSales({
      variables: {
        leadCurrentStageId: lead.stage.id,
        leadCategoryId: leadCategoryId,
      },
    });
  };

  const handleConfirmMoveToSales = () => {
    // Get the presales form UUID from leadFormDetails
    const presalesFormUuid = lead?.leadFormDetails?.formUuid;
    if (!presalesFormUuid) {
      messageApi.error("Could not find the lead form UUID. Cannot move lead.");
      setMoveToSalesConfirmOpen(false);
      return;
    }
    moveLeadToSales({
      variables: {
        presalesFormUuid: presalesFormUuid,
      },
    });
  };

  const {
    data: allActivitiesData,
    loading: allActivitiesLoading,
    error: allActivitiesError,
  } = useQuery(GET_ALL_ACTIVITIES, {
    variables: { leadCategoryId: leadCategoryId },
    skip: !leadCategoryId,
  });

  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
    refetch: activityDataRefetch,
    fetchMore,
  } = useQuery(GET_LEAD_ACTIVITY, {
    variables: { leadId: leadId, pagingCriteria: pagingCriteria },
  });

  const timelineData = activityData?.getLeadsAndActivityLogs?.data || [];
  cursorValue = activityData?.getLeadsAndActivityLogs?.pageMeta?.nextPage;

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [editingRemarksId, setEditingRemarksId] = useState(null);
  const [editingRemarksValue, setEditingRemarksValue] = useState("");
  const [editingRemarksLogType, setEditingRemarksLogType] = useState(null);

  const istToUtc = (date, time) => {
    const combined = dayjs(date)
      .hour(dayjs(time).hour())
      .minute(dayjs(time).minute())
      .second(0);
    const utcDateTime = combined.tz("Asia/Kolkata", true).utc();

    return utcDateTime.toISOString();
  };

  const getIconForQuestion = (questionLabel) => {
    const label = (questionLabel || "").trim().toLowerCase();

    if (label.includes("service line") || label.includes("serviceline")) {
      return (
        <Briefcase className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("business unit") || label.includes("businessunit")) {
      return (
        <Building2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      );
    }

    if (
      label.includes("phone") ||
      label.includes("mobile") ||
      label.includes("contact")
    ) {
      return <Phone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />;
    }
    if (label.includes("email")) {
      return <Mail className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />;
    }
    if (label.includes("company") || label.includes("business")) {
      return (
        <Building2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("address") || label.includes("city") || label.includes("state")) {
      return (
        <MapPin className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("remark") || label.includes("description")) {
      return (
        <MessageSquareText className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
      );
    }
    if (label.includes("generated") || label.includes("server")) {
      return <Cpu className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />;
    }

    if (label.includes("linkedin")) {
      return (
        <LinkedInIcon
          fontSize="small"
          className="text-indigo-600 mt-0.5 flex-shrink-0"
        />
      );
    }

    if (label.includes("category")) {
      return (
        <LabelIcon
          fontSize="small"
          className="text-indigo-600 mt-0.5 flex-shrink-0"
        />
      );
    }

    // if (label.includes("city") || label.includes("state")) {
    //   return (
    //     <LocationOnIcon
    //       fontSize="small"
    //       className="text-indigo-600 mt-0.5 flex-shrink-0"
    //     />
    //   );
    // }

    return <User className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />;
  };

  const handleEditRemarks = (logId, currentRemarks, logType) => {
    setEditingRemarksId(logId);
    setEditingRemarksValue(currentRemarks);
    setEditingRemarksLogType(logType);
  };

  const handleSaveRemarks = () => {
    if (!editingRemarksValue.trim()) {
      messageApi.error("Remarks cannot be empty");
      return;
    }

    updateStageRemarks({
      variables: {
        leadStageLogId: editingRemarksId,
        remarks: editingRemarksValue,
      },
    });
  };

  const handleCancelEditRemarks = () => {
    setEditingRemarksId(null);
    setEditingRemarksValue("");
    setEditingRemarksLogType(null);
  };

  const resetModalState = () => {
    setActivityModalOpen(false);
    setSelectedActivity(null);
    setSelectedStatus(null);
    setRemarks("");
    setCallDuration("");
    setMeetingDate("");
    setMeetingStartTime("");
    setMeetingEndTime("");
    setMeetingLocation("");
    setMeetingMomUpload(null);
    setIsEditMode(false);
    setEditingActivity(null);
    setIsScheduled(false);
    setEntryType(null);
    setLogEntryDate(dayjs());
    setLogEntryTime(dayjs());
  };

  const handleActivityClick = (activity) => {
    setPendingActivity(activity);
    setEntryTypeModalOpen(true);
  };

  const handleNavigateToResearchSection = () => {
    if (!leadExternalDetails || leadExternalDetails.length === 0) return;

    navigate("/research", {
      state: {
        leadId: lead.id,
        leadExternalDetails, // 👈 your array
      },
    });
  };

  const handleEntryTypeSelect = (type) => {
    setEntryTypeModalOpen(false);
    setSelectedActivity(pendingActivity);
    setEntryType(type);
    setIsScheduled(type === "scheduled");

    if (type === "log") {
      setLogEntryDate(dayjs());
      setLogEntryTime(dayjs());
    }

    if (
      pendingActivity.activityName?.toLowerCase() !== "remarks" &&
      type === "log"
    ) {
      getActivityStatus({ variables: { activityId: pendingActivity.id } });
    }

    setActivityModalOpen(true);
  };

  if (loading || activityLoading) {
    return (
      <div className="mt-[85px] ml-[75px] p-4">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || activityError) {
    return (
      <div className="mt-[85px] ml-[75px] p-4">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">
            Error loading lead details:{" "}
            {error?.message || activityError?.message}
          </div>
        </div>
      </div>
    );
  }

  const lead = data?.getLeadForLeadId;
  const leadExternalDetails = lead?.leadExternalDetails ?? [];

  if (!lead) return null;

  const displayFields =
    lead.leadFormDetails?.questions?.filter((q) => q.userResponse?.response) ||
    [];

  const mainField = displayFields[0];
  const fullName = mainField?.userResponse?.response || "N/A";

  const formatDate = (dateString) => {
    return dateString.split("T")[0];
  };

  const getInitials = (name) => {
    if (!name || name === "N/A") return "NA";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const fetchMoreData = () => {
    cursorValue = activityData?.getLeadsAndActivityLogs?.pageMeta?.nextPage;
    fetchMore({
      variables: {
        leadId,
        pagingCriteria: {
          limit: limit,
          cursor: cursorValue,
          sortByField: "created_at",
          sortOrder: "desc",
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        return {
          getLeadsAndActivityLogs: {
            ...fetchMoreResult.getLeadsAndActivityLogs,
            data: [
              ...(prev.getLeadsAndActivityLogs?.data || []),
              ...(fetchMoreResult.getLeadsAndActivityLogs?.data || []),
            ],
          },
        };
      },
    });
  };

  const parseAndExtractValue = (response, viewValue) => {
    if (!response) return "N/A";

    if (typeof response === "string") {
      if (response.startsWith("{") && response.includes("response:")) {
        const match = response.match(/response:\s*([^,}]+)/i);
        if (match) {
          let value = match[1].trim();
          value = value.replace(/[,}]+$/, "").trim();
          return value;
        }
      }

      if (!response.includes("{") && !response.includes(":")) {
        return response;
      }
    }

    if (viewValue) {
      try {
        let parsedResponse;

        if (typeof response === "string") {
          parsedResponse = JSON.parse(response);
        } else {
          parsedResponse = response;
        }

        if (parsedResponse && typeof parsedResponse === "object") {
          return parsedResponse[viewValue] || response;
        }
      } catch {
        return response;
      }
    }

    return response;
  };

  return (
    <div className="mt-[85px] ml-[75px] p-4 min-h-screen bg-[#f4f4f4] global">
      {contextHolder}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px" }}>
        <BackButton label="Back" />
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Lead Details Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 primary rounded-lg flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0">
              {getInitials(fullName)}
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                {fullName}
              </h1>

              <div className="space-y-2 text-base text-gray-600">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 custom:gap-20">
                  <div className="flex items-center gap-2 ">
                    <span className="font-medium">Assigned User:</span>
                    <span>{lead.assignedUser?.name || "Not Assigned"}</span>
                  </div>

                  {lead?.assignedDate && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Date Assigned:</span>
                      <span>{formatDate(lead.assignedDate || "N/A")}</span>
                    </div>
                  )}

                  {lead?.leadSourcedFrom && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Lead Source:</span>
                      <span>{lead.leadSourcedFrom}</span>
                    </div>
                  )}
                </div>

                {displayFields.slice(1).map((field) => {
                  const label = field.question.toLowerCase();

                  return (
                    <div key={field.id} className="flex items-start gap-2">
                      {getIconForQuestion(field.question)}
                      <div>
                        <span className="font-medium text-gray-700">
                          {field.question}:{" "}
                        </span>
                        {(() => {
                          const value = parseAndExtractValue(
                            field.userResponse.response,
                            field.viewValue
                          );

                          if (!value) {
                            return <span className="text-gray-600">N/A</span>;
                          }

                          const isUrlField =
                            typeof field.reValidation === "string" &&
                            field.reValidation.startsWith("^https?:\\/\\/");

                          if (isUrlField) {
                            return (
                              <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center gap-1"
                                title={value}
                              >
                                Link
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4m-4-4l8-8m0 0H8m8 0v8"
                                  />
                                </svg>
                              </a>
                            );
                          }

                          return <span className="text-gray-600">{value}</span>;
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            <div className="primary text-white px-4 py-3 rounded-lg text-center min-w-[140px]">
              <div className="text-sm font-medium">Lead Received</div>
              <div className="text-lg font-bold mt-1">
                {formatDate(lead.leadCreatedAt)}
              </div>
            </div>

            {leadExternalDetails.length > 0 && (
              <button
                onClick={handleNavigateToResearchSection}
                className="
      flex items-center justify-center gap-2
      w-full min-w-[140px]
      px-4 py-2
      rounded-lg
      border border-[#c12328]
      text-[#c12328] font-medium
      hover:bg-primary hover:text-[#c12328]
      transition-all
    "
              >
                Research Section
              </button>
            )}

            {/* Move to Sales Button (Only visible if leadCategoryId is 1) */}
            {String(leadCategoryId) === "1" || String(leadCategoryId) === "6" && (
              <>
                <button
                  onClick={handleMoveToSalesClick}
                  disabled={validating || movingToSales}
                  className="
        flex items-center justify-center gap-2
        w-full min-w-[140px]
        px-4 py-2
        rounded-lg
        bg-[#c12328] text-white font-medium
        hover:bg-[#a01f23]
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-all
      "
                >
                  {validating ? "Checking..." : "Move to Sales"}
                </button>

                {/* Inline error below the button */}
                {moveToSalesError && (
                  <p className="text-red-600 text-xs text-center mt-1 max-w-[160px]">
                    {moveToSalesError}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stages and Status Section */}
      <div className="mt-6">
        <StagesStatusSection
          leadId={leadId}
          leadCategoryId={leadCategoryId}
          stage={lead?.stage}
          status={lead?.status}
          onUpdate={() => {
            // Refetch both lead details (to get updated stage.id) and activity timeline
            refetchLeadDetails();
            activityDataRefetch();
          }}
        />
      </div>

      {/* Activities Section */}
      {allActivitiesData && !allActivitiesLoading && (
        <div className="mt-6">
          <ActivitiesSection
            allActivitiesData={allActivitiesData}
            onActivityClick={handleActivityClick}
          />
        </div>
      )}

      {/* Timeline Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-[#393939] mb-2">
            Timeline
          </h2>
        </div>

        {activityLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-500">Loading timeline...</div>
          </div>
        ) : (
          <>
            <ActivityTimeline
              data={timelineData}
              onEditRemarks={handleEditRemarks}
              onEditActivity={(activityLog) => {
                setIsEditMode(true);
                setEditingActivity(activityLog);
                setSelectedActivity(activityLog.activity);
                setRemarks(activityLog.remarks || "");
                setSelectedStatus(activityLog.activityStatus || null);

                // If it's a backdated entry, populate the log entry date/time
                if (activityLog.backDatedEntry && activityLog.backDatedEntry) {
                  const actionDate = dayjs(activityLog.backDatedEntry);
                  setLogEntryDate(actionDate);
                  setLogEntryTime(actionDate);
                  setEntryType("log");
                }

                if (
                  activityLog.callDurationSeconds &&
                  activityLog.activity.activityName === "call"
                ) {
                  const minutes = Math.floor(
                    activityLog.callDurationSeconds / 60
                  );
                  const seconds = activityLog.callDurationSeconds % 60;
                  setCallDuration(
                    `${String(minutes).padStart(2, "0")}:${String(
                      seconds
                    ).padStart(2, "0")}`
                  );
                }

                if (
                  activityLog.meeting &&
                  activityLog.activity.activityName === "meeting"
                ) {
                  const startDate = new Date(
                    activityLog.meeting.meetingStartDatetime
                  );
                  const endDate = new Date(
                    activityLog.meeting.meetingEndDatetime
                  );

                  setMeetingDate(startDate.toISOString().split("T")[0]);

                  const startHours = String(startDate.getHours()).padStart(
                    2,
                    "0"
                  );
                  const startMinutes = String(startDate.getMinutes()).padStart(
                    2,
                    "0"
                  );
                  setMeetingStartTime(`${startHours}:${startMinutes}`);

                  const endHours = String(endDate.getHours()).padStart(2, "0");
                  const endMinutes = String(endDate.getMinutes()).padStart(
                    2,
                    "0"
                  );
                  setMeetingEndTime(`${endHours}:${endMinutes}`);

                  setMeetingLocation(activityLog.meeting.location || "");
                }

                setActivityModalOpen(true);
                if (
                  activityLog.activity.activityName?.toLowerCase() !== "remarks"
                ) {
                  getActivityStatus({
                    variables: { activityId: activityLog.activity.id },
                  });
                }
              }}
            />
            {cursorValue != null && (
              <button onClick={fetchMoreData} className="mt-4 text-red-500">
                Load More
              </button>
            )}
          </>
        )}
      </div>

      {/* Entry Type Selection Modal */}
      <EntryTypeModal
        open={entryTypeModalOpen}
        onClose={() => {
          setEntryTypeModalOpen(false);
          setPendingActivity(null);
        }}
        onSelectType={handleEntryTypeSelect}
      />

      {/* Activity Modal */}
      <ActivityModal
        open={activityModalOpen}
        onClose={resetModalState}
        activity={selectedActivity}
        statusData={statusData}
        statusLoading={statusLoading}
        savingActivity={savingActivity}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        remarks={remarks}
        setRemarks={setRemarks}
        callDuration={callDuration}
        setCallDuration={setCallDuration}
        meetingDate={meetingDate}
        setMeetingDate={setMeetingDate}
        meetingStartTime={meetingStartTime}
        setMeetingStartTime={setMeetingStartTime}
        meetingEndTime={meetingEndTime}
        setMeetingEndTime={setMeetingEndTime}
        meetingLocation={meetingLocation}
        setMeetingLocation={setMeetingLocation}
        meetingMomUpload={meetingMomUpload}
        setMeetingMomUpload={setMeetingMomUpload}
        existingMeetingMomFilepath={editingActivity?.meetingMomFilepath || ""}
        isScheduled={isScheduled}
        isEditMode={isEditMode}
        entryType={entryType}
        logEntryDate={logEntryDate}
        logEntryTime={logEntryTime}
        onLogEntryDateChange={setLogEntryDate}
        onLogEntryTimeChange={setLogEntryTime}
        onSave={async () => {
          const activityName = selectedActivity.activityName.toLowerCase();

          const mutationVariables = {
            leadId: leadId,
            activityId: selectedActivity.id,
            activityMethod: selectedActivity.activityMethod,
            remarks: remarks,
          };

          // Add logged datetime for log entry
          if (entryType === "log" && !isEditMode) {
            const actionDateTime = istToUtc(logEntryDate, logEntryTime);
            mutationVariables.activityTimestamp = actionDateTime;
            mutationVariables.backDatedEntry = true;
          }

          if (isEditMode && editingActivity) {
            mutationVariables.activityLogId = editingActivity.id;

            if (activityName === "meeting" && editingActivity.meeting) {
              mutationVariables.meetingId = editingActivity.meeting.id;
            }
          }

          if (!isScheduled && activityName !== "remarks" && selectedStatus) {
            mutationVariables.activityStatusId = selectedStatus.id;
          }

          if (!isScheduled && activityName === "call" && callDuration) {
            const parts = callDuration.split(":");
            const minutes = parseInt(parts[0] || 0);
            const seconds = parseInt(parts[1] || 0);
            mutationVariables.callDurationSeconds = minutes * 60 + seconds;
          }

          if (isScheduled || activityName === "meeting") {
            const startDateTime = new Date(
              `${meetingDate}T${meetingStartTime}:00`
            );
            const endDateTime = new Date(`${meetingDate}T${meetingEndTime}:00`);

            mutationVariables.meetingStartDatetime =
              startDateTime.toISOString();
            mutationVariables.meetingEndDatetime = endDateTime.toISOString();

            if (!isScheduled && activityName === "meeting") {
              mutationVariables.location = meetingLocation;
            }

            mutationVariables.meetingType = "scheduled";
          }

          if (activityName === "meeting" && meetingMomUpload) {
            mutationVariables.meetingMomUpload = meetingMomUpload;
          }

          try {
            const result = await addUpdateLeadActivity({
              variables: mutationVariables,
            });

            resetModalState();
          } catch (error) {
            console.error("Mutation failed:", error);
          }
        }}
      />

      {/* Edit Remarks Dialog */}
      <Dialog
        open={editingRemarksId !== null}
        onClose={handleCancelEditRemarks}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Inter", fontWeight: 600, pb: 0.5 }}>
          Edit Remarks
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter remarks here..."
            value={editingRemarksValue}
            onChange={(e) => setEditingRemarksValue(e.target.value)}
            variant="outlined"
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                fontFamily: "Inter",
                color: "#333",
                backgroundColor: "#fff",
                "& fieldset": {
                  borderColor: "#c12328",
                },
                "&:hover fieldset": {
                  borderColor: "#a01f23",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#c12328",
                  borderWidth: "2px",
                },
              },
              "& .MuiOutlinedInput-input": {
                color: "#333",
                fontSize: "0.95rem",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={handleCancelEditRemarks}
            sx={{
              color: "#666",
              textTransform: "none",
              fontFamily: "Inter",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRemarks}
            disabled={updatingRemarks}
            sx={{
              bgcolor: "#c12328",
              color: "white",
              textTransform: "none",
              fontFamily: "Inter",
              "&:hover": {
                bgcolor: "#a01f23",
              },
              "&.Mui-disabled": {
                bgcolor: "#ccc",
                color: "#999",
              },
            }}
          >
            {updatingRemarks ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to Sales Confirmation Dialog */}
      <Dialog
        open={moveToSalesConfirmOpen}
        onClose={() => !movingToSales && setMoveToSalesConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Inter", fontWeight: 600, pb: 0.5 }}>
          Move Lead to Sales
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            sx={{ fontFamily: "Inter", color: "#444", fontSize: "0.95rem" }}
          >
            Are you sure you want to move this lead to&nbsp;<strong>Sales</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3, gap: 1 }}>
          <Button
            onClick={() => setMoveToSalesConfirmOpen(false)}
            disabled={movingToSales}
            sx={{
              color: "#666",
              textTransform: "none",
              fontFamily: "Inter",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMoveToSales}
            disabled={movingToSales}
            variant="contained"
            sx={{
              bgcolor: "#c12328",
              color: "white",
              textTransform: "none",
              fontFamily: "Inter",
              "&:hover": { bgcolor: "#a01f23" },
              "&.Mui-disabled": { bgcolor: "#ccc", color: "#999" },
            }}
          >
            {movingToSales ? "Moving..." : "Yes, Move"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LeadDetails;
